/**
 * ReviewSurface — the review session's binding of the shared shell.
 *
 * The shell itself is `shared/surface/DetailPageSurface`. This file used to
 * hold a near-identical copy of it, which had drifted in two ways that showed
 * on screen: no pane splitters between stacked panes, and a canvas inset
 * horizontally only — so with nothing docked the content ran flush into the
 * window edge while the pane column stayed a contained card, and the canvas
 * sat 12px above the panes' top edge. Both are fixed by sharing.
 *
 * The pane CONTENT is still passed in by `ReviewSessionDetail`, so the
 * findings pane (list AND the finding being read) and the document list stay
 * where they are and nothing here grows domain logic. The canvas it wraps is
 * the DOCUMENT — see `ReviewDocumentCanvas`.
 */

import React from 'react';
import DetailPageSurface, {
  DetailPane,
} from '../../shared/surface/DetailPageSurface';
import useDetailSurface from '../../shared/surface/useDetailSurface';
import { UsePanelSurfaceResult } from '../../shared/surface/usePanelSurface';
import { REVIEW_SURFACE_DEFINITION, ReviewPanelId } from './layout';

/** The session owns the layout; this is the hook it should use. */
export const useReviewPanels = (): UsePanelSurfaceResult<ReviewPanelId> =>
  useDetailSurface<ReviewPanelId>(REVIEW_SURFACE_DEFINITION).panels;

/** Rail pills for every pane that asked for one, straight from the config. */
export const reviewRailItems = REVIEW_SURFACE_DEFINITION.railItems;

export type ReviewPane = DetailPane<ReviewPanelId>;

interface Props {
  /**
   * The surface's layout state.
   *
   * Owned by the PARENT, not this shell: opening a document is an action of
   * the session (a reviewer clicked "view document"), and the pane has to
   * appear in response. A shell that hid its own state could not be asked.
   */
  layoutApi: UsePanelSurfaceResult<ReviewPanelId>;
  panes: ReviewPane[];
  /** The canvas — the selected finding and its decisions. */
  children: React.ReactNode;
  /** Height of the shell, so its children can scroll inside it. */
  height?: string;
}

const ReviewSurface: React.FC<Props> = ({ layoutApi, panes, children, height }) => (
  <DetailPageSurface<ReviewPanelId>
    surface={REVIEW_SURFACE_DEFINITION}
    layoutApi={layoutApi}
    panes={panes}
    height={height}
    /*
      The canvas IS the document, so it is the paper — not the app's soft
      `bg-bg` showing through around a white sheet — and it runs to the shell's
      edge on every side no pane is docked to. Same treatment drafting gives
      its page, and for the same reason.
    */
    canvasClassName="bg-white dark:bg-surface"
    canvasBleed
  >
    {children}
  </DetailPageSurface>
);

export default ReviewSurface;
